import { css } from '@mui/material'
import {
  CSSProperties,
  InputHTMLAttributes,
  KeyboardEvent,
  ReactNode
} from 'react'
import Check from 'assets/icons/nucleo/18px_check.svg'
import { Button, ButtonVariant } from 'components/common/Button'
import { Loader } from 'components/common/Loader'
import {
  InputErrorLabel,
  InputLabel,
  InputLabelBlock,
  StyledTextInput,
  SymbolContainer,
  TextInputWrapper
} from 'components/common/TextInput/TextInput.style'
import { FlexContainer } from 'components/common/common.style'
import { theme } from 'styles/theme'

export interface TextInputProps {
  fullSize?: boolean
  inputLabel?: string | ReactNode
  errorMessage?: string | ReactNode
  inputProps: InputHTMLAttributes<HTMLInputElement>
  placeholder?: string
  disabled?: boolean
  button?: {
    label?: string | ReactNode
    onClick?: (e: unknown) => void
    disableEnterAction?: boolean
    isLoading?: boolean
    isFinished?: boolean
    style?: CSSProperties
    popover?: { label: string }
    disabled?: boolean
    type: 'button' | 'submit'
    noBackground?: boolean
    noBorder?: boolean
    size?: 'small' | 'medium'
    marginLeft?: string
  } | null
  optionalLabel?: string
  lowercase?: boolean
  uppercase?: boolean
  noMargin?: boolean
  leftSymbol?: string
  noRadius?: boolean
  noFocus?: boolean
  paddedError?: boolean
  whiteBackground?: boolean
  noBorder?: boolean
  noPadding?: boolean
  noErrorBorder?: boolean
  tooltip?: ReactNode
  inputRef?: React.RefObject<HTMLInputElement | null>
  autoFocus?: boolean
}

export const TextInput = ({
  fullSize,
  inputLabel,
  errorMessage,
  inputProps,
  placeholder,
  disabled = false,
  button,
  lowercase,
  uppercase = false,
  noMargin = false,
  leftSymbol,
  noRadius,
  noFocus,
  paddedError,
  whiteBackground,
  noPadding,
  noBorder,
  noErrorBorder,
  tooltip,
  inputRef,
  autoFocus
}: TextInputProps) => {
  return (
    <InputLabelBlock fullSize={fullSize} noMargin={noMargin}>
      {inputLabel && (
        <InputLabel>
          {inputLabel} {tooltip}
        </InputLabel>
      )}
      <FlexContainer
        css={css`
          align-items: center;
        `}
      >
        <TextInputWrapper
          hasLeftSymbol={leftSymbol !== undefined}
          error={noErrorBorder ? false : !!errorMessage}
          noRadius={noRadius}
          noFocus={noFocus}
          disabled={disabled}
          $whiteBackground={whiteBackground}
          noBorder={noBorder}
        >
          {leftSymbol && <SymbolContainer>{leftSymbol}</SymbolContainer>}
          <StyledTextInput
            noPadding={noPadding}
            placeholder={placeholder}
            disabled={disabled}
            lowercase={lowercase}
            uppercase={uppercase}
            ref={inputRef}
            autoFocus={autoFocus}
            onKeyDown={(e: KeyboardEvent<HTMLInputElement>) => {
              if (
                e.key === 'Enter' &&
                button &&
                button.onClick &&
                !button.disableEnterAction
              ) {
                if (button.disabled) return
                e.stopPropagation()
                return button.onClick(e)
              }
            }}
            {...inputProps}
          />
        </TextInputWrapper>
        {button && (
          <Button
            noBackground={button.noBackground}
            noBorder={button.noBorder}
            style={button.style}
            disabled={button.isFinished || button.isLoading || button.disabled}
            label={button.label}
            popover={button.popover}
            icon={
              !button.isFinished && button.isLoading ? (
                <Loader />
              ) : button.isFinished ? (
                <Check
                  css={css`
                    path {
                      stroke: ${theme.palette.icon.green};
                    }
                  `}
                />
              ) : undefined
            }
            type={button.type ?? 'button'}
            variant={
              button.isFinished
                ? ButtonVariant.Secondary
                : ButtonVariant.Quaternary
            }
            size={button.size}
            onClick={button.onClick}
            css={css`
              margin-left: ${button.marginLeft ?? '0'};
            `}
          />
        )}
      </FlexContainer>
      {errorMessage && (
        <InputErrorLabel padded={paddedError}>{errorMessage}</InputErrorLabel>
      )}
    </InputLabelBlock>
  )
}
